import { useEffect, DependencyList, useState } from "react";

import firebase from "firebaseFactory";
import { FirebaseShard } from "firebaseWrapper";

interface StreamLiveDataOptions<T> {
  isPaused?: boolean;
  maxDataPoints: number;
  orderByDescending: boolean;
  firebaseRef: string;
  firebaseShard: FirebaseShard;
  onInitialData: (data: T[]) => void;
  onIncrementalData: (callback: (currentData: T[]) => T[]) => void;
}

export const useLiveData = <T>(
  { onInitialData, ...options }: StreamLiveDataOptions<T>,
  dependencies: DependencyList
) => {
  const [isLoading, setIsLoading] = useState(true);
  useEffect(() => {
    let unsubscribe: VoidFunction;
    (async () => {
      unsubscribe = await streamLiveData<T>({
        onInitialData: (data) => {
          onInitialData(data);
          setIsLoading(false);
        },
        ...options,
      });
    })();
    return () => unsubscribe && unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options.isPaused, ...dependencies]);
  return { isLoading };
};

const streamLiveData = async <T>({
  isPaused,
  maxDataPoints,
  orderByDescending,
  firebaseRef,
  firebaseShard,
  onInitialData,
  onIncrementalData,
}: StreamLiveDataOptions<T>) => {
  if (isPaused)
    return () => {
      /* no op */
    };
  const ref = firebase.database(firebaseShard).ref(firebaseRef).orderByKey();
  // load initial data in one go
  const initialData = ((
    await ref.limitToLast(maxDataPoints).once("value")
  ).val() || {}) as { [key: string]: T };

  const initialDataTimebasedKeys = orderByDescending
    ? Object.keys(initialData).reverse()
    : Object.keys(initialData);
  onInitialData(
    orderByDescending
      ? Object.values(initialData).reverse()
      : Object.values(initialData)
  );
  let lastDataPoint = "0";
  if (initialDataTimebasedKeys.length > 0) {
    lastDataPoint = initialDataTimebasedKeys[
      orderByDescending ? 0 : initialDataTimebasedKeys.length - 1
    ].toString();
    console.log(`starting at ${lastDataPoint}`);
  }
  // we want the *next* item after the last data point
  // adding 0 to the end should of the string should make
  // sure we always get the following entry
  const subscriptionRef = ref.startAt(lastDataPoint + "0");
  // then stream
  const listener = subscriptionRef.on(
    "child_added",
    async (messageDataSnapshot: firebase.database.DataSnapshot) => {
      const newDataPoint = messageDataSnapshot.val();
      onIncrementalData((currentData: unknown[]) =>
        orderByDescending
          ? [newDataPoint, ...currentData.slice(0, maxDataPoints)]
          : [
              ...currentData.slice(
                currentData.length - maxDataPoints,
                currentData.length
              ),
              newDataPoint,
            ]
      );
    }
  );
  return () => subscriptionRef.off("child_added", listener);
};
